import Konva from "konva";
import { solidOf } from "../../lib/position-tone.js";
import type { PlayerRow } from "../../repos/players.js";
import { makeKonvaRenderer, type PageDef, type PageRenderer } from "../canvas-kit/export/index.js";
import soccerBallUrl from "./assets/soccer-ball.svg?url";
import {
  BOARD_WIDTH,
  type BoardSnapshot,
  boardDimensions,
  type FieldKind,
  type NameDisplay,
  type Shape,
  type Stroke,
  type TextAnnotation,
  TOKEN_SIZE_PRESET_FACTOR,
  type Token,
  type TokenSizePreset,
  tokenDisplayName,
} from "./scene.js";

/**
 * Snapshot type accepted by the tactics renderer. The renderer needs the
 * player roster to resolve avatars/numbers/position tones for player
 * tokens — opponents and balls draw without lookup.
 */
export interface TacticsExportSnapshot {
  snapshot: BoardSnapshot;
  players: Map<string, PlayerRow>;
}

/**
 * Build Konva nodes for one tactics page (one frame).
 *
 * The frame to render is selected via `page.payload.frameIndex`, which
 * `framesAsPages` sets up. Pitch is drawn as a simplified imperative
 * version of the live `<Pitch>` component (background fill + outer line
 * + center line + center circle) — full pitch markings can be added
 * incrementally without breaking the renderer contract.
 */
function buildTacticsNodes({
  snapshot,
  page,
  layer,
}: {
  snapshot: TacticsExportSnapshot;
  page: PageDef;
  layer: Konva.Layer;
}): Promise<void> {
  const { snapshot: snap, players } = snapshot;
  const idx = (page.payload as { frameIndex?: number } | undefined)?.frameIndex ?? 0;
  const frame = snap.frames[idx] ?? snap.frames[0];
  if (!frame) return Promise.resolve();

  const { width, height } = boardDimensions(snap.orientation, snap.fieldKind);
  addPitch(layer, snap.fieldKind, width, height);

  for (const s of frame.scene.strokes) addStroke(layer, s);
  for (const s of frame.scene.shapes) addShape(layer, s);
  for (const t of frame.scene.texts) addText(layer, t);
  // Tokens may need to await image loads (ball sprite, player avatars).
  // Resolve all of them before the caller draws the layer to PNG.
  const sizePreset: TokenSizePreset = snap.tokenSizePreset ?? "normal";
  const nameDisplay: NameDisplay = snap.nameDisplay ?? "full";
  return Promise.all(
    frame.scene.tokens.map((tk) =>
      addToken(layer, tk, players, snap.fieldKind, sizePreset, nameDisplay),
    ),
  ).then(() => undefined);
}

export const tacticsRenderer: PageRenderer<TacticsExportSnapshot> =
  makeKonvaRenderer(buildTacticsNodes);

/* ─── pitch (full markings — mirrors features/tactics/pitch.tsx) ──── */

const PITCH_COLORS = {
  grass: "#1f6e3a",
  grassAlt: "#22773f",
  paper: "#ffffff",
  ink: "rgba(15,18,22,0.85)",
  inkSoft: "rgba(15,18,22,0.35)",
  zoneTint: "rgba(15,18,22,0.04)",
  line: "rgba(255,255,255,0.85)",
} as const;

function addPitch(layer: Konva.Layer, kind: FieldKind, w: number, h: number) {
  const isPaper = kind === "pitch_blank" || kind === "pitch_zones";
  const bg = isPaper ? PITCH_COLORS.paper : PITCH_COLORS.grass;
  const lineColor = isPaper ? PITCH_COLORS.ink : PITCH_COLORS.line;
  const half = kind === "pitch_half";
  const isLandscape = w >= h;
  const cornerRadius = Math.min(w, h) * 0.025;

  // Outer pitch group with rounded clip so stripes / markings don't bleed
  // past the rounded surface corners (mirrors the live Pitch component).
  const group = new Konva.Group({
    listening: false,
    clipFunc: (ctx) => {
      const r = cornerRadius;
      ctx.beginPath();
      ctx.moveTo(r, 0);
      ctx.lineTo(w - r, 0);
      ctx.quadraticCurveTo(w, 0, w, r);
      ctx.lineTo(w, h - r);
      ctx.quadraticCurveTo(w, h, w - r, h);
      ctx.lineTo(r, h);
      ctx.quadraticCurveTo(0, h, 0, h - r);
      ctx.lineTo(0, r);
      ctx.quadraticCurveTo(0, 0, r, 0);
      ctx.closePath();
    },
  });
  layer.add(group);

  // Surface
  group.add(
    new Konva.Rect({
      x: 0,
      y: 0,
      width: w,
      height: h,
      fill: bg,
      cornerRadius,
      listening: false,
    }),
  );

  // Mowing stripes (green pitches only)
  if (!isPaper) {
    const stripeW = w / 10;
    for (let i = 0; i < 10; i += 1) {
      group.add(
        new Konva.Rect({
          x: i * stripeW,
          y: 0,
          width: stripeW,
          height: h,
          fill: i % 2 === 0 ? PITCH_COLORS.grass : PITCH_COLORS.grassAlt,
          opacity: 0.55,
          listening: false,
        }),
      );
    }
  }

  // Zones overlay (paper-style 6×6 grid + labels)
  if (kind === "pitch_zones") {
    addZonesGrid(group, w, h, isLandscape);
  }

  // Pitch markings (penalty boxes, goal boxes, spots, arcs, center line/circle)
  addPitchMarkings(group, w, h, lineColor, half, isLandscape);
}

function addPitchMarkings(
  group: Konva.Group,
  width: number,
  height: number,
  lineColor: string,
  half: boolean,
  isLandscape: boolean,
) {
  const stroke = 4;
  // Markings are sized against the FULL pitch length, not the board's actual
  // long side, so a half pitch shows real proportions (penalty box ~31% of the
  // half's length instead of ~16%). `longRef` is therefore 1920 regardless of
  // orientation or field kind — identical to the live `<PitchMarkings>`
  // component. Deriving it from `width`/`height` instead would make exported
  // half pitches disagree with what the editor shows.
  const longRef = BOARD_WIDTH;
  const SCALE = 1.25;
  const penaltyDepth = Math.round(longRef * 0.115 * SCALE);
  const penaltyWidth = Math.round((isLandscape ? height : width) * 0.42 * SCALE);
  const goalDepth = Math.round(longRef * 0.042 * SCALE);
  const goalWidth = Math.round((isLandscape ? height : width) * 0.18 * SCALE);
  const arcRadius = Math.round(longRef * 0.063 * SCALE);
  const centerRadius = Math.round(longRef * 0.063 * SCALE);
  const spotRadius = 6;
  const margin = 20;

  const spotOffset = Math.round(penaltyDepth * 0.65);
  const spotToBoxEdge = penaltyDepth - spotOffset;
  const halfAngleDeg = Math.min(
    89,
    (Math.acos(Math.min(1, spotToBoxEdge / arcRadius)) * 180) / Math.PI,
  );
  const arcSweep = halfAngleDeg * 2;
  const perpCenter = isLandscape ? height / 2 : width / 2;

  const addStrokeRect = (x: number, y: number, w: number, h: number) =>
    group.add(
      new Konva.Rect({
        x,
        y,
        width: w,
        height: h,
        stroke: lineColor,
        strokeWidth: stroke,
        listening: false,
      }),
    );
  const addCircleStroke = (cx: number, cy: number, r: number) =>
    group.add(
      new Konva.Circle({
        x: cx,
        y: cy,
        radius: r,
        stroke: lineColor,
        strokeWidth: stroke,
        listening: false,
      }),
    );
  const addSpot = (cx: number, cy: number) =>
    group.add(
      new Konva.Circle({
        x: cx,
        y: cy,
        radius: spotRadius,
        fill: lineColor,
        listening: false,
      }),
    );
  const addLine = (pts: number[]) =>
    group.add(
      new Konva.Line({
        points: pts,
        stroke: lineColor,
        strokeWidth: stroke,
        listening: false,
      }),
    );
  const addArc = (cx: number, cy: number, rotation: number, angle: number) =>
    group.add(
      new Konva.Arc({
        x: cx,
        y: cy,
        innerRadius: arcRadius - stroke / 2,
        outerRadius: arcRadius + stroke / 2,
        angle,
        rotation,
        fill: lineColor,
        listening: false,
      }),
    );

  // Outer rectangle
  addStrokeRect(margin, margin, width - margin * 2, height - margin * 2);

  // Center line + circle + spot (full pitch only)
  if (!half) {
    if (isLandscape) {
      addLine([width / 2, margin, width / 2, height - margin]);
    } else {
      addLine([margin, height / 2, width - margin, height / 2]);
    }
    addCircleStroke(width / 2, height / 2, centerRadius);
    addSpot(width / 2, height / 2);
  }

  if (isLandscape) {
    // Right (attacking) end — always
    addStrokeRect(
      width - margin - penaltyDepth,
      perpCenter - penaltyWidth / 2,
      penaltyDepth,
      penaltyWidth,
    );
    addStrokeRect(width - margin - goalDepth, perpCenter - goalWidth / 2, goalDepth, goalWidth);
    addSpot(width - margin - Math.round(penaltyDepth * 0.65), perpCenter);
    addArc(width - margin - spotOffset, perpCenter, 180 - halfAngleDeg, arcSweep);
    if (!half) {
      addStrokeRect(margin, perpCenter - penaltyWidth / 2, penaltyDepth, penaltyWidth);
      addStrokeRect(margin, perpCenter - goalWidth / 2, goalDepth, goalWidth);
      addSpot(margin + Math.round(penaltyDepth * 0.65), perpCenter);
      addArc(margin + spotOffset, perpCenter, -halfAngleDeg, arcSweep);
    }
  } else {
    // Top (attacking) end — always
    addStrokeRect(perpCenter - penaltyWidth / 2, margin, penaltyWidth, penaltyDepth);
    addStrokeRect(perpCenter - goalWidth / 2, margin, goalWidth, goalDepth);
    addSpot(perpCenter, margin + Math.round(penaltyDepth * 0.65));
    addArc(perpCenter, margin + spotOffset, 90 - halfAngleDeg, arcSweep);
    if (!half) {
      addStrokeRect(
        perpCenter - penaltyWidth / 2,
        height - margin - penaltyDepth,
        penaltyWidth,
        penaltyDepth,
      );
      addStrokeRect(perpCenter - goalWidth / 2, height - margin - goalDepth, goalWidth, goalDepth);
      addSpot(perpCenter, height - margin - Math.round(penaltyDepth * 0.65));
      addArc(perpCenter, height - margin - spotOffset, 270 - halfAngleDeg, arcSweep);
    }
  }

  // Halfway line on the open side of a half pitch
  if (half) {
    if (isLandscape) {
      addLine([margin, margin, margin, height - margin]);
      group.add(
        new Konva.Arc({
          x: margin,
          y: perpCenter,
          innerRadius: centerRadius - stroke / 2,
          outerRadius: centerRadius + stroke / 2,
          angle: 180,
          rotation: -90,
          fill: lineColor,
          listening: false,
        }),
      );
      addSpot(margin, perpCenter);
    } else {
      addLine([margin, height - margin, width - margin, height - margin]);
      group.add(
        new Konva.Arc({
          x: width / 2,
          y: height - margin,
          innerRadius: centerRadius - stroke / 2,
          outerRadius: centerRadius + stroke / 2,
          angle: 180,
          rotation: 180,
          fill: lineColor,
          listening: false,
        }),
      );
      addSpot(width / 2, height - margin);
    }
  }
}

function addZonesGrid(group: Konva.Group, width: number, height: number, isLandscape: boolean) {
  const margin = 20;
  const tickInset = margin + 6;
  const longSide = isLandscape ? width : height;
  const shortSide = isLandscape ? height : width;
  const SCALE = 1.25;
  const penaltyDepth = Math.round(longSide * 0.115 * SCALE);
  const penaltyWidth = Math.round(shortSide * 0.42 * SCALE);
  const goalWidth = Math.round(shortSide * 0.18 * SCALE);

  const shortCenter = shortSide / 2;
  const shortBounds = [
    margin,
    shortCenter - penaltyWidth / 2,
    shortCenter - goalWidth / 2,
    shortCenter,
    shortCenter + goalWidth / 2,
    shortCenter + penaltyWidth / 2,
    shortSide - margin,
  ];
  const longLeftPenalty = margin + penaltyDepth;
  const longRightPenalty = longSide - margin - penaltyDepth;
  const longCenter = longSide / 2;
  const longBounds = [
    margin,
    longLeftPenalty,
    (longLeftPenalty + longCenter) / 2,
    longCenter,
    (longCenter + longRightPenalty) / 2,
    longRightPenalty,
    longSide - margin,
  ];
  const xBounds = isLandscape ? longBounds : shortBounds;
  const yBounds = isLandscape ? shortBounds : longBounds;

  const numbers = ["1", "2", "3", "4", "5", "6"];
  const letters = ["A", "B", "C", "D", "E", "F"];
  const xLabels = isLandscape ? numbers : letters;
  const yLabels = isLandscape ? letters : numbers;

  let smallestSpan = Infinity;
  for (let i = 0; i < 6; i += 1) {
    const dx = xBounds[i + 1]! - xBounds[i]!;
    const dy = yBounds[i + 1]! - yBounds[i]!;
    smallestSpan = Math.min(smallestSpan, dx, dy);
  }
  const labelFont = Math.max(10, Math.round(smallestSpan * 0.45));

  // Checkerboard tint
  for (let r = 0; r < 6; r += 1) {
    for (let c = 0; c < 6; c += 1) {
      if ((r + c) % 2 !== 1) continue;
      group.add(
        new Konva.Rect({
          x: xBounds[c]!,
          y: yBounds[r]!,
          width: xBounds[c + 1]! - xBounds[c]!,
          height: yBounds[r + 1]! - yBounds[r]!,
          fill: PITCH_COLORS.zoneTint,
          listening: false,
        }),
      );
    }
  }
  // Vertical dashed grid lines
  for (let i = 0; i < 5; i += 1) {
    const x = xBounds[i + 1]!;
    group.add(
      new Konva.Line({
        points: [x, margin, x, height - margin],
        stroke: PITCH_COLORS.inkSoft,
        strokeWidth: 1.5,
        dash: [6, 6],
        listening: false,
      }),
    );
  }
  // Horizontal dashed grid lines
  for (let i = 0; i < 5; i += 1) {
    const y = yBounds[i + 1]!;
    group.add(
      new Konva.Line({
        points: [margin, y, width - margin, y],
        stroke: PITCH_COLORS.inkSoft,
        strokeWidth: 1.5,
        dash: [6, 6],
        listening: false,
      }),
    );
  }
  // Column labels (top + bottom)
  xLabels.forEach((label, i) => {
    const centerX = (xBounds[i]! + xBounds[i + 1]!) / 2;
    const common = {
      width: labelFont * 2,
      height: labelFont,
      text: label,
      fontSize: labelFont,
      fontStyle: "500",
      fill: PITCH_COLORS.inkSoft,
      align: "center" as const,
      verticalAlign: "middle" as const,
      listening: false,
    };
    group.add(new Konva.Text({ ...common, x: centerX - labelFont, y: tickInset }));
    group.add(
      new Konva.Text({
        ...common,
        x: centerX - labelFont,
        y: height - tickInset - labelFont,
      }),
    );
  });
  // Row labels (left + right)
  yLabels.forEach((label, i) => {
    const centerY = (yBounds[i]! + yBounds[i + 1]!) / 2;
    const common = {
      width: labelFont * 2,
      height: labelFont,
      text: label,
      fontSize: labelFont,
      fontStyle: "500",
      fill: PITCH_COLORS.inkSoft,
      align: "center" as const,
      verticalAlign: "middle" as const,
      listening: false,
    };
    group.add(
      new Konva.Text({
        ...common,
        x: tickInset,
        y: centerY - labelFont / 2,
      }),
    );
    group.add(
      new Konva.Text({
        ...common,
        x: width - tickInset - labelFont * 2,
        y: centerY - labelFont / 2,
      }),
    );
  });
}

/* ─── tokens ──────────────────────────────────────────────── */

const TOKEN_RADIUS_BASE = 38;
const BALL_RADIUS_BASE = 22;

function tokenRadiusFor(token: Token, preset: TokenSizePreset): number {
  const base = token.kind === "ball" ? BALL_RADIUS_BASE : TOKEN_RADIUS_BASE;
  const presetMul = TOKEN_SIZE_PRESET_FACTOR[preset] ?? 1;
  const tokenMul = token.scale ?? 1;
  return base * presetMul * tokenMul;
}

/**
 * Promise-based image loader for the export pipeline. Konva's normal flow
 * (`useImage` in components) is async + render-driven; the export renderer
 * runs once off-screen and needs the bitmap up front so `stage.toCanvas`
 * snapshots the real image instead of an empty placeholder.
 * Resolves to `null` on error so we can fall back to a placeholder without
 * crashing the export.
 */
function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const el = new window.Image();
    el.crossOrigin = "anonymous";
    el.onload = () => resolve(el);
    el.onerror = () => resolve(null);
    el.src = url;
  });
}

async function addToken(
  layer: Konva.Layer,
  token: Token,
  players: Map<string, PlayerRow>,
  fieldKind: FieldKind,
  sizePreset: TokenSizePreset,
  nameDisplay: NameDisplay,
): Promise<void> {
  const radius = tokenRadiusFor(token, sizePreset);
  if (token.kind === "ball") {
    // Mirror the live `BallToken`: try the bundled soccer-ball SVG, fall
    // back to a plain white disc if the load fails (offline export etc.).
    const img = await loadImage(soccerBallUrl);
    if (img) {
      layer.add(
        new Konva.Image({
          image: img,
          x: token.x - radius,
          y: token.y - radius,
          width: radius * 2,
          height: radius * 2,
          shadowColor: "black",
          shadowBlur: 6,
          shadowOpacity: 0.35,
          shadowOffsetY: 2,
          listening: false,
        }),
      );
    } else {
      layer.add(
        new Konva.Circle({
          x: token.x,
          y: token.y,
          radius,
          fill: "#ffffff",
          stroke: "#0b1220",
          strokeWidth: 2,
          listening: false,
        }),
      );
    }
    return;
  }

  const player = token.playerId ? players.get(token.playerId) : null;
  const solid =
    token.kind === "opponent"
      ? { fill: token.color ?? "#dc2626", stroke: "#3a0808", fg: "#ffffff" }
      : solidOf(player?.position);
  layer.add(
    new Konva.Circle({
      x: token.x,
      y: token.y,
      radius,
      fill: solid.fill,
      stroke: solid.stroke,
      strokeWidth: 3,
      listening: false,
    }),
  );

  // Try to render the player avatar (mirrors live `<AvatarClip>`) — clipped
  // to a circle just inside the token's stroke. Falls back to the jersey
  // number / initials when there's no photo or the load fails.
  const avatarUrl = token.kind === "player" ? (player?.image_ref ?? null) : null;
  const avatarImg = avatarUrl ? await loadImage(avatarUrl) : null;

  if (avatarImg) {
    const r = radius - 4;
    const clipGroup = new Konva.Group({
      x: token.x,
      y: token.y,
      clipFunc: (ctx) => {
        ctx.arc(0, 0, r, 0, Math.PI * 2, false);
      },
      listening: false,
    });
    clipGroup.add(
      new Konva.Image({
        image: avatarImg,
        x: -r,
        y: -r,
        width: r * 2,
        height: r * 2,
        listening: false,
      }),
    );
    layer.add(clipGroup);

    // The avatar hides the jersey number — surface it in an overlapping badge
    // at the bottom-left corner (mirrors the live `<NumberBadge>`).
    const badgeNumber =
      token.number != null
        ? token.number
        : token.kind === "player" && player?.shirt_number != null
          ? player.shirt_number
          : null;
    if (badgeNumber != null) {
      const br = radius * 0.5;
      const offset = radius * 0.66;
      const bx = token.x - offset;
      const by = token.y + offset;
      layer.add(
        new Konva.Circle({
          x: bx,
          y: by,
          radius: br,
          fill: solid.fill,
          stroke: solid.stroke,
          strokeWidth: 2,
          listening: false,
        }),
      );
      layer.add(
        new Konva.Text({
          x: bx - br,
          y: by - br,
          width: br * 2,
          height: br * 2,
          align: "center",
          verticalAlign: "middle",
          text: String(badgeNumber),
          fontSize: br * 1.15,
          fontStyle: "700",
          fill: solid.fg,
          listening: false,
        }),
      );
    }
  } else {
    const number =
      token.number != null
        ? String(token.number)
        : player?.shirt_number != null
          ? String(player.shirt_number)
          : "";
    const text = number || initialsOf(token.label ?? player?.name ?? "");
    if (text) {
      const fontScale = radius / TOKEN_RADIUS_BASE;
      layer.add(
        new Konva.Text({
          x: token.x - radius,
          y: token.y - radius,
          width: radius * 2,
          height: radius * 2,
          align: "center",
          verticalAlign: "middle",
          text,
          fontSize: (number ? 30 : 24) * fontScale,
          fontStyle: "700",
          fill: solid.fg,
          listening: false,
        }),
      );
    }
  }

  // Name-plate text: abbreviation applies to real roster players only; dummies
  // and opponents keep their own labels (see `tokenDisplayName`).
  const label = tokenDisplayName(token, player?.name ?? null, nameDisplay);
  if (label) {
    const nameColor =
      fieldKind === "pitch_blank" || fieldKind === "pitch_zones" ? "#0b1220" : "#ffffff";
    const fontScale = radius / TOKEN_RADIUS_BASE;
    layer.add(
      new Konva.Text({
        x: token.x - radius * 2,
        y: token.y + radius + 6,
        width: radius * 4,
        align: "center",
        text: label,
        fontSize: 18 * fontScale,
        fontStyle: "600",
        fill: nameColor,
        listening: false,
      }),
    );
  }
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/* ─── primitives (mirror whiteboard renderer) ─────────────── */

function addStroke(layer: Konva.Layer, st: Stroke) {
  layer.add(
    new Konva.Line({
      points: st.points,
      stroke: st.color,
      strokeWidth: st.width,
      tension: 0.35,
      lineCap: "round",
      lineJoin: "round",
      dash: st.dashed ? [st.width * 3, st.width * 2] : undefined,
      listening: false,
    }),
  );
}

function addShape(layer: Konva.Layer, sh: Shape) {
  const dash = sh.dashed ? [sh.width * 3, sh.width * 2] : undefined;
  switch (sh.kind) {
    case "line":
      layer.add(
        new Konva.Line({
          x: sh.x,
          y: sh.y,
          points: [0, 0, sh.w, sh.h],
          stroke: sh.color,
          strokeWidth: sh.width,
          dash,
          lineCap: "round",
          listening: false,
        }),
      );
      break;
    case "arrow":
      layer.add(
        new Konva.Arrow({
          x: sh.x,
          y: sh.y,
          points: [0, 0, sh.w, sh.h],
          stroke: sh.color,
          fill: sh.color,
          strokeWidth: sh.width,
          dash,
          pointerLength: sh.width * 4,
          pointerWidth: sh.width * 4,
          listening: false,
        }),
      );
      break;
    case "rect":
      layer.add(
        new Konva.Rect({
          x: sh.x,
          y: sh.y,
          width: sh.w,
          height: sh.h,
          stroke: sh.color,
          strokeWidth: sh.width,
          dash,
          cornerRadius: 6,
          listening: false,
        }),
      );
      break;
    case "circle":
      layer.add(
        new Konva.Circle({
          x: sh.x,
          y: sh.y,
          radius: Math.max(Math.abs(sh.w), 1),
          stroke: sh.color,
          strokeWidth: sh.width,
          dash,
          listening: false,
        }),
      );
      break;
  }
}

function addText(layer: Konva.Layer, tx: TextAnnotation) {
  layer.add(
    new Konva.Text({
      x: tx.x,
      y: tx.y,
      text: tx.value,
      fontSize: tx.fontSize,
      fontStyle: "700",
      fill: tx.color,
      listening: false,
    }),
  );
}
